import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import { formatDateTimeToDDMMMYYYY, formatDateToDDMMMYYYY } from '../../utils/dateFormatter';
import { formatPageTitle } from '../../utils/pageTitles';
import { useForumAccess } from '../../hooks/useForumAccess';
import { listForums, getRecentForumComments } from '../../services/ForumService';
import { usePageToolbar } from '../layouts/nav/PageToolbarContext';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import '../Documents/commonstyle.css';
import './forum.css';
import './forumDashboard.css';

function sortActiveDiscussions(items) {
  return [...(items || [])]
    .filter((item) => item && item.status !== 'closed')
    .sort((a, b) => {
      const aTime = a.last_comment_at
        ? new Date(a.last_comment_at).getTime()
        : a.created_at
          ? new Date(a.created_at).getTime()
          : 0;
      const bTime = b.last_comment_at
        ? new Date(b.last_comment_at).getTime()
        : b.created_at
          ? new Date(b.created_at).getTime()
          : 0;
      if (bTime !== aTime) return bTime - aTime;
      const statusRank = (s) => (s === 'ongoing' ? 0 : s === 'on_hold' ? 1 : 2);
      return statusRank(a.status) - statusRank(b.status);
    })
    .slice(0, 3);
}

const ForumDashboard = () => {
  const { hasAccess, loading: accessLoading } = useForumAccess();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [discussions, setDiscussions] = useState([]);
  const [comments, setComments] = useState([]);

  useEffect(() => {
    document.title = formatPageTitle('Forum Dashboard');
  }, []);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [forumsRes, commentsRes] = await Promise.allSettled([
        listForums({ page: 1, limit: 50 }),
        getRecentForumComments({ limit: 10 }),
      ]);

      if (forumsRes.status === 'fulfilled') {
        const items = forumsRes.value?.data?.items || [];
        setDiscussions(sortActiveDiscussions(items));
      } else {
        setDiscussions([]);
      }

      if (commentsRes.status === 'fulfilled') {
        setComments(commentsRes.value?.data?.items || []);
      } else {
        setComments([]);
      }

      if (forumsRes.status === 'rejected' && commentsRes.status === 'rejected') {
        setError('Failed to load forum dashboard');
      }
    } catch (err) {
      console.error('Forum dashboard load failed', err);
      setError(err?.message || 'Failed to load forum dashboard');
      setDiscussions([]);
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!accessLoading && hasAccess) {
      loadDashboard();
    }
  }, [accessLoading, hasAccess, loadDashboard]);

  const discussionCards = useMemo(() => discussions, [discussions]);

  const forumDashboardToolbar = useMemo(() => {
    if (accessLoading || !hasAccess) return null;
    return (
      <>
        <Link
          to="/forum/list"
          className="header-toolbar-btn"
          title="All forums"
          aria-label="All forums"
        >
          <i className="fas fa-comments" aria-hidden="true" />
        </Link>
        <Link to="/forum/create" className="toolbar-add-btn" title="Start conversation">
          <i className="fas fa-plus" aria-hidden="true" />
          <span>Start conversation</span>
        </Link>
      </>
    );
  }, [accessLoading, hasAccess]);

  usePageToolbar(forumDashboardToolbar, [forumDashboardToolbar]);

  if (accessLoading) {
    return <LoadingSpinner label="Loading…" />;
  }

  if (!hasAccess) {
    return null;
  }

  if (loading) {
    return <LoadingSpinner label="Loading…" />;
  }

  return (
    <div className="forum-dashboard">
      {error ? <div className="forum-dashboard__error">{error}</div> : null}

      <section className="forum-dashboard__section">
        <div className="forum-dashboard__section-head">
          <h2>Top Ongoing Discussions</h2>
          <span>Your busiest open conversations right now</span>
        </div>
        {discussionCards.length ? (
          <div className="forum-dashboard__discussion-grid">
            {discussionCards.map((forum, index) => (
              <Link
                key={forum.id}
                to={`/forum/detail/${forum.id}`}
                className="fd-discussion-card"
              >
                <div className="fd-discussion-card__accent" aria-hidden="true" />
                <div className="fd-discussion-card__top">
                  <div className="fd-discussion-card__icon" aria-hidden="true">
                    <i className="fas fa-comments" />
                  </div>
                  <div className="fd-discussion-card__chips">
                    <span className={`forum-status-badge forum-status-${forum.status}`}>
                      {String(forum.status || '').replace('_', ' ')}
                    </span>
                    {forum.priority ? (
                      <span className={`fd-priority fd-priority--${String(forum.priority).toLowerCase()}`}>
                        {forum.priority}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="fd-discussion-card__body">
                  <span className="fd-discussion-card__id">#{forum.id}</span>
                  <h3 className="fd-discussion-card__title" title={forum.title}>
                    {forum.title || 'Untitled discussion'}
                  </h3>
                  <div className="fd-discussion-card__meta">
                    <div className="fd-discussion-card__meta-item">
                      <span className="fd-discussion-card__meta-label">Created by</span>
                      <span className="fd-discussion-card__meta-value">
                        {forum.creator?.full_name || '—'}
                      </span>
                    </div>
                    <div className="fd-discussion-card__meta-item">
                      <span className="fd-discussion-card__meta-label">Started</span>
                      <span className="fd-discussion-card__meta-value">
                        {forum.start_date
                          ? formatDateToDDMMMYYYY(forum.start_date)
                          : forum.created_at
                            ? formatDateToDDMMMYYYY(forum.created_at)
                            : '—'}
                      </span>
                    </div>
                    <div className="fd-discussion-card__meta-item">
                      <span className="fd-discussion-card__meta-label">Last activity</span>
                      <span className="fd-discussion-card__meta-value">
                        {forum.last_comment_at
                          ? formatDateTimeToDDMMMYYYY(forum.last_comment_at)
                          : 'No comments yet'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="fd-discussion-card__footer">
                  <span className="fd-discussion-card__last-by">
                    {forum.last_comment_by
                      ? `Last comment by ${forum.last_comment_by}`
                      : 'Waiting for the first comment'}
                  </span>
                  <span className="fd-discussion-card__rank" aria-hidden="true">
                    {index + 1}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          !loading && (
            <div className="forum-dashboard__empty">
              No open discussions right now.{' '}
              <Link to="/forum/create">Start a conversation</Link>
            </div>
          )
        )}
      </section>

      <section className="forum-dashboard__section">
        <div className="forum-dashboard__section-head forum-dashboard__section-head--row">
          <div>
            <h2>Recent Comments &amp; Replies</h2>
            <span>The latest 10 updates from your discussions</span>
          </div>
          <Link to="/forum/list" className="forum-dashboard__inline-link">
            Browse forums
          </Link>
        </div>
        {comments.length ? (
          <div className="forum-dashboard__table-wrap">
            <table className="forum-dashboard__table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Discussion</th>
                  <th>Type</th>
                  <th>Author</th>
                  <th>Summary</th>
                </tr>
              </thead>
              <tbody>
                {comments.map((comment) => {
                  const summary =
                    comment.subject ||
                    comment.content_preview ||
                    '—';
                  return (
                    <tr key={comment.id}>
                      <td className="forum-dashboard__table-when">
                        {comment.created_at
                          ? formatDateTimeToDDMMMYYYY(comment.created_at)
                          : '—'}
                      </td>
                      <td>
                        <Link
                          to={`/forum/detail/${comment.forum_id}#comment-${comment.id}`}
                          className="forum-dashboard__table-link"
                          title={comment.forum_title || undefined}
                        >
                          {comment.forum_title || `Forum #${comment.forum_id}`}
                        </Link>
                      </td>
                      <td>
                        <span
                          className={`forum-dashboard__type${
                            comment.is_reply ? ' is-reply' : ''
                          }`}
                        >
                          {comment.is_reply ? 'Reply' : 'Comment'}
                        </span>
                      </td>
                      <td>{comment.author?.full_name || '—'}</td>
                      <td>
                        <Link
                          to={`/forum/detail/${comment.forum_id}#comment-${comment.id}`}
                          className="forum-dashboard__table-summary"
                          title={summary}
                        >
                          {summary}
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          !loading && (
            <div className="forum-dashboard__empty">
              No recent comments yet.{' '}
              <Link to="/forum/list">Open a discussion</Link>
            </div>
          )
        )}
      </section>

      <BackToTop />
      <InternalFooter />
    </div>
  );
};

export default ForumDashboard;
